<template>
  <div class="page-about">
  <main>
      <section class="relative overflow-hidden bg-gray-50">
        <div class="absolute inset-0 bg-gradient-to-r from-brand/5 via-transparent to-brand/5"></div>
        <div class="mx-auto max-w-[1400px] px-4 py-16 lg:flex lg:items-center lg:gap-12 lg:px-8 lg:py-20">
          <div class="relative z-10 space-y-4 lg:w-7/12">
            <p class="text-xs font-semibold uppercase text-muted">Our story</p>
            <h1 class="text-3xl font-semibold leading-tight text-ink sm:text-4xl lg:text-5xl">We are the event and invite marketplace built for clarity, trust, and joy.</h1>
            <p class="text-lg text-muted">Invi8 was founded by planners and engineers who wanted to replace endless calls and hidden fees with transparent pricing, live availability, and vetted vendors for every kind of gathering.</p>
            <div class="flex flex-wrap gap-3">
              <span class="rounded-full bg-white px-4 py-2 text-sm font-semibold text-brand shadow-card ring-1 ring-gray-100">Launched 2024</span>
              <span class="rounded-full bg-white px-4 py-2 text-sm font-semibold text-ink shadow-card ring-1 ring-gray-100">300+ vendors</span>
              <span class="rounded-full bg-white px-4 py-2 text-sm font-semibold text-ink shadow-card ring-1 ring-gray-100">USA · Global soon</span>
            </div>
          </div>
          <div class="relative z-10 mt-10 flex-1 lg:mt-0">
            <div class="rounded-3xl bg-white p-6 shadow-card ring-1 ring-gray-100">
              <img class="h-64 w-full rounded-2xl object-cover" src="https://dummyimage.com/720x480/e7f7ff/111827" alt="Team">
              <div class="mt-4 grid gap-3 sm:grid-cols-2">
                <div class="rounded-2xl bg-gray-50 p-4">
                  <p class="text-sm font-semibold text-ink">Built with hosts</p>
                  <p class="text-sm text-muted">We co-created features with corporate teams, campuses, and social hosts in SF, NY, and Austin.</p>
                </div>
                <div class="rounded-2xl bg-gray-50 p-4">
                  <p class="text-sm font-semibold text-ink">Vendors first</p>
                  <p class="text-sm text-muted">We keep payouts fast and contracts transparent for both sides.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
        <div class="grid gap-6 md:grid-cols-3">
          <div class="rounded-3xl bg-gray-50 p-6 ring-1 ring-gray-100">
            <p class="text-sm font-semibold text-ink">Transparency</p>
            <p class="mt-2 text-sm text-muted">Clear pricing, no surprises. Every listing includes packages, add-ons, and availability.</p>
          </div>
          <div class="rounded-3xl bg-gray-50 p-6 ring-1 ring-gray-100">
            <p class="text-sm font-semibold text-ink">Trust</p>
            <p class="mt-2 text-sm text-muted">We vet contracts, insurance, and reviews before vendors go live.</p>
          </div>
          <div class="rounded-3xl bg-gray-50 p-6 ring-1 ring-gray-100">
            <p class="text-sm font-semibold text-ink">Delight</p>
            <p class="mt-2 text-sm text-muted">Concierge support that moves fast—book holds, compare options, and stay relaxed.</p>
          </div>
        </div>
      </section>

      <section class="bg-gray-900">
        <div class="mx-auto max-w-[1400px] px-4 py-12 text-white lg:px-8">
          <div class="grid gap-8 lg:grid-cols-2 lg:items-center">
            <div class="space-y-4">
              <p class="text-xs font-semibold uppercase text-gray-400">Milestones</p>
              <h2 class="text-3xl font-semibold leading-tight">We’re just getting started.</h2>
              <p class="text-lg text-gray-300">Our promise: keep the experience simple, modern, and human.</p>
              <div class="grid gap-4">
                <div class="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                  <p class="text-sm text-gray-200">Average vendor response</p>
                  <p class="text-lg font-semibold text-white">2 hours</p>
                </div>
                <div class="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                  <p class="text-sm text-gray-200">Contracts reviewed</p>
                  <p class="text-lg font-semibold text-white">480+</p>
                </div>
                <div class="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                  <p class="text-sm text-gray-200">Avg. savings per event</p>
                  <p class="text-lg font-semibold text-white">$2.1k</p>
                </div>
              </div>
            </div>
            <div class="rounded-3xl bg-white p-6 text-ink shadow-card ring-1 ring-gray-100">
              <p class="text-sm font-semibold text-ink">Our principles</p>
              <ul class="mt-3 space-y-3 text-sm text-muted">
                <li class="flex gap-3">
                  <span class="mt-1 h-2 w-2 rounded-full bg-brand"></span>
                  We build for clarity—no hidden fees, no bait-and-switch packages.
                </li>
                <li class="flex gap-3">
                  <span class="mt-1 h-2 w-2 rounded-full bg-brand"></span>
                  Vendors deserve great tools, fast payouts, and supportive partners.
                </li>
                <li class="flex gap-3">
                  <span class="mt-1 h-2 w-2 rounded-full bg-brand"></span>
                  Couples deserve to book with confidence and enjoy the process.
                </li>
              </ul>
              <div class="mt-6 rounded-2xl bg-gray-50 p-4">
                <p class="text-sm font-semibold text-ink">Want to partner?</p>
                <p class="text-sm text-muted">We’re building out new markets. Join our vendor waitlist.</p>
                <div class="mt-3 flex flex-col gap-3 sm:flex-row">
                  <input class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm text-ink placeholder:text-muted focus:border-brand focus:outline-none" placeholder="Business email" />
                  <button class="rounded-2xl bg-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-brandDark">Join list</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
        <div class="grid gap-6 md:grid-cols-2">
          <div class="rounded-3xl bg-gray-50 p-6 ring-1 ring-gray-100">
            <p class="text-xs font-semibold uppercase text-muted">For hosts & planners</p>
            <h3 class="text-2xl font-semibold text-ink">Plan with less stress.</h3>
            <p class="mt-2 text-sm text-muted">Shortlists, holds, and clear contracts for every event from one dashboard.</p>
            <ul class="mt-4 space-y-2 text-sm text-muted">
              <li>• Concierge recommendations in 24 hours</li>
              <li>• Transparent pricing and add-ons across vendors</li>
              <li>• Saved favorites and live availability by date</li>
            </ul>
            <button class="mt-4 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark">Start planning</button>
          </div>
          <div class="rounded-3xl bg-gray-900 p-6 text-white">
            <p class="text-xs font-semibold uppercase text-gray-400">For vendors</p>
            <h3 class="text-2xl font-semibold">Grow with Invi8.</h3>
            <p class="mt-2 text-sm text-gray-300">Beautiful listings, real guests, and fast payouts.</p>
            <ul class="mt-4 space-y-2 text-sm text-gray-300">
              <li>• Highlight packages and add-ons</li>
              <li>• Calendar sync and instant holds</li>
              <li>• A support team that cares</li>
            </ul>
            <button class="mt-4 rounded-full bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:-translate-y-0.5 hover:shadow-card">Apply to join</button>
          </div>
        </div>
      </section>

      <section class="bg-gray-50">
        <div class="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
          <div class="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div class="space-y-3">
              <p class="text-xs font-semibold uppercase text-muted">Platform benefits</p>
              <h2 class="text-2xl font-semibold text-ink">One place for invites, RSVPs, and vendors.</h2>
              <ul class="mt-2 space-y-2 text-sm text-muted">
                <li>• Dashboards that sync invites, RSVPs, vendor holds, and payments</li>
                <li>• Mobile-first invites with desktop-perfect layouts and brand controls</li>
                <li>• Transparent pricing, availability, and backup options via concierge</li>
              </ul>
            </div>
            <div class="overflow-hidden rounded-3xl shadow-card ring-1 ring-gray-100">
              <img src="https://dummyimage.com/900x620/f4f6ff/111827&text=Invi8+Dashboard" alt="Invi8 dashboard" class="h-full w-full object-cover">
            </div>
          </div>
        </div>
      </section>

      <section class="bg-white">
        <div class="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
          <div class="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div class="overflow-hidden rounded-3xl shadow-card ring-1 ring-gray-100">
              <img src="https://dummyimage.com/900x620/fff1f5/111827&text=Host+%2B+Vendor+Flow" alt="How Invi8 works" class="h-full w-full object-cover">
            </div>
            <div class="space-y-4">
              <p class="text-xs font-semibold uppercase text-muted">How Invi8 works</p>
              <ol class="space-y-2 text-sm text-muted list-decimal pl-4">
                <li>Create or pick an invite for the event type—add dates, venue, co-hosts.</li>
                <li>Search vendors, compare packages, and place refundable holds.</li>
                <li>Share invites, collect RSVPs, and sync updates across guests.</li>
                <li>Lock vendors, finalize contracts, and stay on schedule.</li>
              </ol>
              <div class="grid gap-3 sm:grid-cols-2">
                <div class="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-100">
                  <p class="flex items-center gap-2 text-sm font-semibold text-ink">
                    <i class="ri-hand-coin-line text-brand"></i>
                    Vendor benefits
                  </p>
                  <p class="text-sm text-muted">Verified leads, fast payouts, rich listings, and calendar sync.</p>
                </div>
                <div class="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-100">
                  <p class="flex items-center gap-2 text-sm font-semibold text-ink">
                    <i class="ri-building-4-line text-brand"></i>
                    Venue benefits
                  </p>
                  <p class="text-sm text-muted">Clear event briefs, holds with dates, and on-brand invites that convert.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="bg-gray-50">
        <div class="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
          <div class="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div class="overflow-hidden rounded-3xl bg-white p-4 shadow-card ring-1 ring-gray-100">
              <img :src="activeTab.img" :alt="activeTab.title" class="h-full w-full rounded-2xl object-cover transition">
            </div>
            <div class="space-y-3">
              <button
                v-for="tab in featureTabs"
                :key="tab.id"
                class="flex w-full items-start gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-left text-sm font-semibold text-ink shadow-card transition hover:-translate-y-0.5 hover:border-brand hover:text-brand"
                :class="activeTab.id === tab.id ? 'border-transparent bg-[#2f2f2f] text-white shadow-lg' : ''"
                @click="activeTabId = tab.id"
              >
                <span class="h-10 w-1 rounded-full" :class="activeTab.id === tab.id ? 'bg-white/80' : 'bg-brand/30'"></span>
                <div>
                  <p>{{ tab.title }}</p>
                  <p v-if="activeTab.id === tab.id" class="mt-1 text-xs font-normal" :class="activeTab.id === tab.id ? 'text-gray-200' : 'text-muted'">
                    {{ tab.copy }}
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
export default {
  name: 'AboutView',
  data () {
    return {
      activeTabId: 'sell',
      featureTabs: [
        {
          id: 'sell',
          title: 'Sell photos to event participants',
          copy: 'You can sell photos to event guests to generate revenue for your event.',
          img: 'https://dummyimage.com/900x620/fffaf0/111827&text=Sell+photos+to+guests'
        },
        {
          id: 'liveness',
          title: 'Liveliness Detection',
          copy: 'Keep your gallery trustworthy with automatic checks before publishing.',
          img: 'https://dummyimage.com/900x620/f0f4ff/111827&text=Liveliness+Detection'
        },
        {
          id: 'integration',
          title: 'Website Integration',
          copy: 'Embed invites, galleries, and vendor shortlists directly on your site.',
          img: 'https://dummyimage.com/900x620/f5f3ff/111827&text=Website+Integration'
        },
        {
          id: 'branding',
          title: 'Branding & Promotions',
          copy: 'Lock colors, logos, and promo banners so every invite stays on-brand.',
          img: 'https://dummyimage.com/900x620/e8f7ff/111827&text=Branding+%26+Promotions'
        }
      ]
    }
  },
  computed: {
    activeTab () {
      return this.featureTabs.find(t => t.id === this.activeTabId) || this.featureTabs[0]
    }
  }
}
</script>
